/**
 * ProcessEditor — form-based editor for agent-process definitions.
 *
 * Layout: Basic Information → Steps card (ordered steps) with fixed Respond
 * card at bottom. Each step has a name input, move controls, and configuration.
 * The Respond card is fixed (not removable) and contains tool selection,
 * max iterations, and the goal-check toggle.
 *
 * Validation runs through `sdk.agentProcesses.validate` before every save;
 * field-path issues render inline and block the save. System
 * (read-only) processes render with a copy-to-my-library action.
 * Arbitrary DAGs that don't fit the steps form stay API/JSON-only
 * and render as a read-only graph notice.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  ProcessDefinition,
  ProcessGraph,
  ValidationIssue,
} from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import { getSDK } from '../../services/sdk-instance';
import {
  useAgentProcess,
  useCopyToAccount,
  useCreateAgentProcess,
  useUpdateAgentProcess,
} from '../../hooks/useAgentProcesses';
import { useAgentDefinitions, useSystemAgents } from '../../hooks/useAgentDefinitions';
import {
  compileStepsToGraph,
  graphToSteps,
  type EditorStepNode,
  type CompileOptions,
} from './process-graph';
import { StepConfigForm } from './StepConfigForm';
import { StepPicker } from './StepPicker';
import { ToolSelectionTree } from './ToolSelectionTree';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';
import { PageHeaderBar } from '../layout/PageHeaderBar';

interface EditorNode {
  key: string;
  node: EditorStepNode;
}

interface EditorStep {
  key: string;
  nodes: EditorNode[];
}

type PickerTarget = 'new-step' | number;

let editorKeyCounter = 0;
function nextKey(): string {
  editorKeyCounter += 1;
  return `ek-${editorKeyCounter}`;
}

const inputClass =
  'w-full rounded-md bg-muted px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';

export interface ProcessEditorProps {
  processId?: string;
}

export function ProcessEditor({ processId }: ProcessEditorProps) {
  const navigate = useNavigate();
  const isEditMode = Boolean(processId);
  const { data: existing, isLoading } = useAgentProcess(processId);
  const createProcess = useCreateAgentProcess();
  const updateProcess = useUpdateAgentProcess();
  const copyToAccount = useCopyToAccount();

  const { data: accountAgents } = useAgentDefinitions();
  const { data: systemAgents } = useSystemAgents();

  const personaNames = useMemo(() => {
    const names = new Set<string>();
    for (const a of accountAgents ?? []) names.add(a.name);
    return [...names].sort();
  }, [accountAgents]);

  const systemPersonaNames = useMemo(() => {
    const names = new Set<string>();
    for (const a of systemAgents ?? []) names.add(a.persona.name);
    return [...names].sort();
  }, [systemAgents]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [maxIterations, setMaxIterations] = useState(2500);
  const [excludedTools, setExcludedTools] = useState<string[]>([]);
  const [respondLabel, setRespondLabel] = useState('Respond');
  const [shogunTools, setShogunTools] = useState<boolean | undefined>(undefined);

  const [checksGoal, setChecksGoal] = useState(false);

  const [steps, setSteps] = useState<EditorStep[]>([]);
  const [advancedGraph, setAdvancedGraph] = useState<ProcessGraph | null>(null);
  const [pickerTarget, setPickerTarget] = useState<PickerTarget | null>(null);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const hasInitialized = useRef(false);

  const isSystem = existing?.is_system ?? false;
  const formDisabled = isSystem;

  useEffect(() => {
    hasInitialized.current = false;
  }, [processId]);

  useEffect(() => {
    if (!isEditMode || !existing || hasInitialized.current) return;
    hasInitialized.current = true;
    setName(existing.name);
    setDescription(existing.description);
    const def = existing.definition;

    setChecksGoal(def.checks_goal);

    setMaxIterations(def.response.max_iterations);
    setExcludedTools(def.response.excluded_tools);
    if (def.response.shogun_tools !== undefined) {
      setShogunTools(def.response.shogun_tools);
    }

    const decompiled = graphToSteps(def.graph);
    if (decompiled === null) {
      setAdvancedGraph(def.graph);
      setSteps([]);
      setRespondLabel('Respond');
    } else {
      setRespondLabel(decompiled.respondLabel);
      setSteps(
        decompiled.steps.map((group) => ({
          key: nextKey(),
          nodes: group.map((node) => ({ key: nextKey(), node: structuredClone(node) })),
        })),
      );
    }
  }, [isEditMode, existing]);

  const buildDefinition = (): ProcessDefinition => {
    const compileOptions: CompileOptions = {};
    if (respondLabel.trim() && respondLabel.trim() !== 'Respond') {
      compileOptions.respondLabel = respondLabel.trim();
    }

    return {
      version: 1,
      checks_goal: checksGoal,
      response: {
        max_iterations: maxIterations,
        excluded_tools: excludedTools,
        ...(shogunTools !== undefined ? { shogun_tools: shogunTools } : {}),
      },
      graph: advancedGraph ?? compileStepsToGraph(steps.map((s) => s.nodes.map((n) => n.node)), compileOptions),
    };
  };

  const issuesForNode = (stepIndex: number, nodeIndex: number): string[] => {
    let flatIndex = 0;
    for (let i = 0; i < stepIndex; i++) flatIndex += steps[i].nodes.length;
    flatIndex += nodeIndex;
    const matched: string[] = [];
    for (const issue of issues) {
      const m = /graph\.nodes\[(\d+)\]/.exec(issue.path);
      if (m && parseInt(m[1], 10) === flatIndex) matched.push(issue.message);
    }
    return matched;
  };

  const insertPicked = (node: EditorStepNode) => {
    if (pickerTarget === 'new-step') {
      setSteps((prev) => [...prev, { key: nextKey(), nodes: [{ key: nextKey(), node }] }]);
    } else if (typeof pickerTarget === 'number') {
      setSteps((prev) =>
        prev.map((s, i) =>
          i === pickerTarget ? { ...s, nodes: [...s.nodes, { key: nextKey(), node }] } : s,
        ),
      );
    }
    setPickerTarget(null);
  };

  const updateNode = (stepIndex: number, nodeIndex: number, node: EditorStepNode) => {
    setSteps((prev) =>
      prev.map((s, i) =>
        i === stepIndex
          ? { ...s, nodes: s.nodes.map((n, j) => (j === nodeIndex ? { ...n, node } : n)) }
          : s,
      ),
    );
  };

  const updateNodeLabel = (stepIndex: number, nodeIndex: number, label: string) => {
    setSteps((prev) =>
      prev.map((s, i) =>
        i === stepIndex
          ? {
              ...s,
              nodes: s.nodes.map((n, j) =>
                j === nodeIndex ? { ...n, node: { ...n.node, label } } : n,
              ),
            }
          : s,
      ),
    );
  };

  const removeNode = (stepIndex: number, nodeIndex: number) => {
    setSteps((prev) =>
      prev
        .map((s, i) => (i === stepIndex ? { ...s, nodes: s.nodes.filter((_, j) => j !== nodeIndex) } : s))
        .filter((s) => s.nodes.length > 0),
    );
  };

  const moveStep = (stepIndex: number, delta: -1 | 1) => {
    setSteps((prev) => {
      const target = stepIndex + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[stepIndex], next[target]] = [next[target], next[stepIndex]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaveError(null);
    setIsSaving(true);
    try {
      const definition = buildDefinition();
      const result = await getSDK().agentProcesses.validate(definition);
      if (!result.valid) {
        setIssues(result.issues);
        return;
      }
      setIssues([]);
      if (isEditMode && processId) {
        await updateProcess.mutateAsync({ processId, updates: { name, description, definition } });
      } else {
        await createProcess.mutateAsync({ name, description, definition });
      }
      void navigate('/agents?tab=processes');
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!processId) return;
    setSaveError(null);
    try {
      const result = await copyToAccount.mutateAsync({
        process_id: processId,
        new_name: `${name} (copy)`,
      });
      if (result.kind === 'process') {
        void navigate(`/agent-processes/${result.process.id}`);
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    }
  };

  if (isEditMode && isLoading) {
    return (
      <LoadingState label="Loading process..." className="h-full" />
    );
  }

  return (
    <form
      data-testid="process-editor"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSave();
      }}
      className="flex h-full flex-col overflow-hidden"
    >
      <PageHeaderBar
        title={isSystem ? name : isEditMode ? 'Edit Process' : 'Create Process'}
        description={isSystem ? 'System process (read-only)' : undefined}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {issues.length > 0 && (
            <div
              data-testid="process-validation-issues"
              className="rounded-md bg-destructive/10 p-3 text-label text-destructive"
            >
              <p className="mb-1 font-medium">The definition has validation issues:</p>
              <ul className="list-inside list-disc space-y-0.5 text-caption">
                {issues.map((issue, i) => (
                  <li key={i}>
                    <span className="font-mono">{issue.path}</span>: {issue.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {saveError && (
            <div
              data-testid="process-save-error"
              className="rounded-md bg-destructive/10 p-3 text-label text-destructive"
            >
              {saveError}
            </div>
          )}

          {/* Basic info */}
          <div className="rounded-lg bg-card p-6">
            <h2 className="mb-4 text-title-2 font-semibold">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-label font-medium">
                  Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  data-testid="process-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={formDisabled}
                  className={inputClass}
                  placeholder="e.g., Research then respond"
                />
              </div>
              <div>
                <label className="mb-2 block text-label font-medium">Description</label>
                <textarea
                  data-testid="process-description-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  disabled={formDisabled}
                  className={inputClass}
                  placeholder="What this process is for..."
                />
              </div>
            </div>
          </div>

          {/* Steps card */}
          <div className="rounded-lg bg-card p-6">
            <h2 className="mb-1 text-title-2 font-semibold">Steps</h2>
            <p className="mb-4 text-caption text-muted-foreground">
              Ordered steps run before the response. Nodes in the same step run in parallel; a
              dynamic step's sub-steps are planned at run time; the fixed Respond step always
              runs last.
            </p>

            {advancedGraph ? (
              <div className="space-y-4">
                <div
                  data-testid="process-advanced-graph-notice"
                  className="rounded-md bg-warning-subtle p-3 text-body text-warning-text"
                >
                  <p className="mb-2">
                    This graph was authored via the API and doesn't fit the steps form. It is
                    preserved as-is on save; edit the graph via the API/JSON.
                  </p>
                  <pre className="max-h-64 overflow-auto rounded bg-muted/50 p-2 text-micro font-mono">
                    {JSON.stringify(advancedGraph, null, 2)}
                  </pre>
                </div>

                {/* Response config for advanced graphs */}
                <RespondCard
                  respondLabel={respondLabel}
                  setRespondLabel={setRespondLabel}
                  excludedTools={excludedTools}
                  setExcludedTools={setExcludedTools}
                  maxIterations={maxIterations}
                  setMaxIterations={setMaxIterations}
                  checksGoal={checksGoal}
                  setChecksGoal={setChecksGoal}
                  shogunTools={shogunTools}
                  setShogunTools={setShogunTools}
                  disabled={formDisabled}
                />
              </div>
            ) : (
              <div className="space-y-3">
                {steps.map((step, stepIndex) => (
                  <div
                    key={step.key}
                    data-testid={`process-step-${stepIndex}`}
                    className="rounded-md bg-background p-3"
                  >
                    <div className="space-y-3">
                      {step.nodes.map((node, nodeIndex) => (
                        <div
                          key={node.key}
                          data-testid={`step-node-${stepIndex}-${nodeIndex}`}
                          className={step.nodes.length > 1 ? 'rounded bg-card p-3' : ''}
                        >
                          {/* Step chrome: name input, badge, controls */}
                          <div className="mb-2 flex items-center gap-2">
                            <input
                              type="text"
                              data-testid={`step-name-${stepIndex}-${nodeIndex}`}
                              value={node.node.label}
                              onChange={(e) => updateNodeLabel(stepIndex, nodeIndex, e.target.value)}
                              disabled={formDisabled}
                              placeholder={`Step ${stepIndex + 1}${step.nodes.length > 1 ? ` (${String.fromCharCode(97 + nodeIndex)})` : ''}`}
                              className="flex-1 rounded bg-muted px-2 py-1 text-label font-medium focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                            />
                            <span className="rounded bg-muted px-1.5 py-0.5 text-micro text-muted-foreground">
                              {node.node.kind === 'dynamic' ? 'Dynamic' : 'Agent'}
                            </span>
                            {!formDisabled && (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => moveStep(stepIndex, -1)}
                                  disabled={stepIndex === 0}
                                  className="rounded px-1.5 text-caption text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
                                  title="Move step up"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveStep(stepIndex, 1)}
                                  disabled={stepIndex === steps.length - 1}
                                  className="rounded px-1.5 text-caption text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
                                  title="Move step down"
                                >
                                  ↓
                                </button>
                                <button
                                  type="button"
                                  data-testid={`step-add-parallel-${stepIndex}`}
                                  onClick={() => setPickerTarget(stepIndex)}
                                  className="rounded px-1.5 py-0.5 text-caption text-primary hover:bg-primary/10"
                                >
                                  + Parallel
                                </button>
                                <button
                                  type="button"
                                  data-testid={`step-remove-${stepIndex}-${nodeIndex}`}
                                  onClick={() => removeNode(stepIndex, nodeIndex)}
                                  className="rounded px-1.5 py-0.5 text-caption text-destructive hover:bg-destructive/10"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>

                          {pickerTarget === stepIndex && nodeIndex === step.nodes.length - 1 && (
                            <div className="mb-2">
                              <StepPicker onPick={insertPicked} onCancel={() => setPickerTarget(null)} />
                            </div>
                          )}

                          {node.node.kind === 'agent' ? (
                            <StepConfigForm
                              config={node.node.config}
                              onChange={(config) =>
                                updateNode(stepIndex, nodeIndex, { kind: 'agent', label: node.node.label, config })
                              }
                              disabled={formDisabled}
                              issues={issuesForNode(stepIndex, nodeIndex)}
                              personaNames={personaNames}
                              systemPersonaNames={systemPersonaNames}
                              testIdPrefix={`step-${stepIndex}-${nodeIndex}`}
                            />
                          ) : (
                            <div
                              data-testid={`dynamic-step-${stepIndex}-${nodeIndex}`}
                              className="space-y-2"
                            >
                              <p className="text-caption text-muted-foreground">
                                Sub-steps planned at run time by the process planner
                              </p>
                              <textarea
                                data-testid={`dynamic-step-prompt-${stepIndex}-${nodeIndex}`}
                                value={node.node.taskPrompt}
                                onChange={(e) =>
                                  updateNode(stepIndex, nodeIndex, {
                                    kind: 'dynamic',
                                    label: node.node.label,
                                    taskPrompt: e.target.value,
                                  })
                                }
                                rows={2}
                                disabled={formDisabled}
                                className={`${inputClass} text-caption font-mono`}
                                placeholder="Optional planning goal — defaults to the user's message. {{user_message}} is substituted."
                              />
                              {issuesForNode(stepIndex, nodeIndex).map((msg, i) => (
                                <p key={i} className="text-caption text-destructive">
                                  {msg}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {pickerTarget === 'new-step' ? (
                  <StepPicker onPick={insertPicked} onCancel={() => setPickerTarget(null)} />
                ) : (
                  !formDisabled && (
                    <button
                      type="button"
                      data-testid="process-add-step"
                      onClick={() => setPickerTarget('new-step')}
                      className="w-full rounded-md bg-primary/5 px-3 py-2 text-body text-primary hover:bg-primary/10"
                    >
                      + Add step
                    </button>
                  )
                )}

                {/* Fixed Respond card at bottom */}
                <RespondCard
                  respondLabel={respondLabel}
                  setRespondLabel={setRespondLabel}
                  excludedTools={excludedTools}
                  setExcludedTools={setExcludedTools}
                  maxIterations={maxIterations}
                  setMaxIterations={setMaxIterations}
                  checksGoal={checksGoal}
                  setChecksGoal={setChecksGoal}
                  shogunTools={shogunTools}
                  setShogunTools={setShogunTools}
                  disabled={formDisabled}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="border-t bg-background px-6 py-4">
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => { void navigate('/agents?tab=processes'); }}>
            {isSystem ? 'Back' : 'Cancel'}
          </Button>
          {isSystem ? (
            <Button
              type="button"
              data-testid="process-copy-button"
              onClick={() => {
                void handleCopy();
              }}
              disabled={copyToAccount.isPending}
            >
              {copyToAccount.isPending ? 'Copying...' : 'Copy to My Library'}
            </Button>
          ) : (
            <Button type="submit" data-testid="process-save-button" disabled={!name || isSaving}>
              {isSaving ? 'Saving...' : isEditMode ? 'Update Process' : 'Create Process'}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
}

interface RespondCardProps {
  respondLabel: string;
  setRespondLabel: (label: string) => void;
  excludedTools: string[];
  setExcludedTools: (tools: string[]) => void;
  maxIterations: number;
  setMaxIterations: (iterations: number) => void;
  checksGoal: boolean;
  setChecksGoal: (checksGoal: boolean) => void;
  shogunTools: boolean | undefined;
  setShogunTools: (tools: boolean | undefined) => void;
  disabled: boolean;
}

function RespondCard({
  respondLabel,
  setRespondLabel,
  excludedTools,
  setExcludedTools,
  maxIterations,
  setMaxIterations,
  checksGoal,
  setChecksGoal,
  shogunTools,
  setShogunTools,
  disabled,
}: RespondCardProps) {
  const smallInputClass =
    'w-full rounded-md bg-muted px-2 py-1 text-caption focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';

  return (
    <div
      data-testid="process-respond-card"
      className="rounded-md bg-primary/10 p-4"
    >
      <div className="mb-3 flex items-center gap-2">
        <input
          type="text"
          data-testid="process-respond-label"
          value={respondLabel}
          onChange={(e) => setRespondLabel(e.target.value)}
          disabled={disabled}
          placeholder="Respond"
          className="flex-1 rounded bg-muted px-2 py-1 text-label font-medium focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        />
        <span className="rounded bg-primary/20 px-1.5 py-0.5 text-micro font-medium text-primary">
          Fixed Final Step
        </span>
      </div>

      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-caption font-medium">Tools</label>
          <ToolSelectionTree
            value={undefined}
            onChange={() => {}}
            mode="response"
            excludedTools={excludedTools}
            onExcludedToolsChange={setExcludedTools}
            disabled={disabled}
            testIdPrefix="process-respond-tools"
          />
        </div>

        <div>
          <label className="mb-1 block text-caption font-medium">Max iterations</label>
          <input
            type="number"
            data-testid="process-max-iterations"
            min={1}
            max={2500}
            value={maxIterations}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              if (Number.isInteger(parsed)) setMaxIterations(parsed);
            }}
            disabled={disabled}
            className={smallInputClass}
          />
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-caption">
          <input
            type="checkbox"
            data-testid="process-checks-goal"
            checked={checksGoal}
            disabled={disabled}
            onChange={(e) => setChecksGoal(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-input"
          />
          Check the goal
        </label>

        {shogunTools !== undefined && (
          <label className="flex cursor-pointer items-center gap-2 text-caption">
            <input
              type="checkbox"
              data-testid="process-shogun-tools"
              checked={shogunTools}
              disabled={disabled}
              onChange={(e) => setShogunTools(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-input"
            />
            Enable Shogun tools
          </label>
        )}
      </div>
    </div>
  );
}
